import React, {
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as _ from 'lodash-es';

import { MappingProperty } from '../../types';

export type MappingPropertyView = MappingProperty & { sheetId: number };

type MappingPropertyContextProps = {
  addMappingProperty: (item: MappingPropertyView) => void;
  deleteMappingProperty: (item: MappingPropertyView) => void;
  restoreMappingProperties: (items: MappingPropertyView[]) => void;
  mappingProperties: MappingPropertyView[];
  mappingPropertiesRef: MutableRefObject<MappingPropertyView[]>;
};

export const MappingPropertyContext =
  createContext<MappingPropertyContextProps>({
    addMappingProperty: () => {},
    deleteMappingProperty: () => {},
    restoreMappingProperties: () => {},
    mappingProperties: [],
    mappingPropertiesRef: { current: [] },
  });

export function MappingPropertyProvider({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const [mappingProperties, setMappingProperties] = useState<
    MappingPropertyView[]
  >([]);

  // spreadjsにバインドした関数内で最新のmappingPropertiesを参照するために、
  // mappingPropertiesのrefバージョンを用意
  const mappingPropertiesRef = useRef(mappingProperties);
  useEffect(() => {
    mappingPropertiesRef.current = mappingProperties;
  }, [mappingProperties]);

  const addMappingProperty = useCallback((item: MappingPropertyView) => {
    setMappingProperties((prev) => [...prev, item]);
  }, []);

  const deleteMappingProperty = useCallback((item: MappingPropertyView) => {
    setMappingProperties((prev) =>
      _.filter(prev, (prevItem) => !_.isEqual(prevItem, item))
    );
  }, []);

  const restoreMappingProperties = useCallback(
    (items: MappingPropertyView[]) => {
      setMappingProperties(items);
    },
    []
  );

  return (
    <MappingPropertyContext.Provider
      value={{
        addMappingProperty,
        deleteMappingProperty,
        restoreMappingProperties,
        mappingProperties,
        mappingPropertiesRef,
      }}
    >
      {children}
    </MappingPropertyContext.Provider>
  );
}

/**
 * フロントエンド用の単一セルとプロパティの紐づけデータ
 */
export const useMappingProperty = () => {
  return useContext(MappingPropertyContext);
};
